import * as React from 'react';
import { path, pathOr } from 'ramda';

import { IBaseQuestionProps, TAnswersState } from '../FormContainer/types/internal';
import { useSimpleForm } from '../../utils/useSimpleForm';
import { QuestionLayoutContainer } from '../QuestionLayoutContainer';
import { RangeQuestion } from '../../components/RangeQuestion';
import { isEmptyString, isValidNumber } from '../../utils/validators';
import { pickOnlyMeaning } from '../../utils/pickOnlyMeaning';
import { useFormTracking } from '../../utils/useFormTracking';
import { validateGte, validateLte } from './utils';
import { removeLeadingZero } from '../../utils/formaters';

export const RangeQuestionContainer: React.FC<React.PropsWithChildren<IBaseQuestionProps>> = props => {
  const {
    question: { answerField, unit, title, required, helpText },
    setAnswersState,
    setNextStep,
    answersState,
  } = props;

  const { trackSubmit, trackChange, trackLteWithoutGte } = useFormTracking();
  const {
    handleSubmit,
    handleChange: onChange,
    values,
    errors,
    isSubmitDisabled,
  } = useSimpleForm<string>({
    onSubmit(values) {
      setAnswersState(prevState => {
        const gte = values['gte'] ? Number(values['gte']) : null;
        const lte = values['lte'] ? Number(values['lte']) : null;

        const nextAnswersState: TAnswersState = {
          ...prevState,
          [answerField]: {
            answerField,
            value: { gte, lte },
          },
        };

        setNextStep(nextAnswersState);
        trackSubmit({
          isRequired: required,
          field: answerField,
          value: `${gte}-${lte}`,
        });

        return nextAnswersState;
      });
    },
    validators: {
      gte: (value, values) => validateGte(value, path(['lte'], values) as string, required),
      lte: (value, values) => validateLte(path(['gte'], values) as string, value, required),
    },
    initialValues: pickOnlyMeaning({
      gte: path([answerField, 'value', 'gte'], answersState),
      lte: path([answerField, 'value', 'lte'], answersState),
    }),
    required,
  });

  const handleChange = React.useCallback<NonNullable<React.ComponentProps<'input'>['onChange']>>(
    e => {
      trackChange();

      const value = e.target.value;

      if (e.target.name === 'lte' && !values['gte']) {
        trackLteWithoutGte({
          isRequired: required,
          field: answerField,
          value,
        });
      }

      if (isValidNumber(value) || isEmptyString(value)) {
        onChange(e.target.name, removeLeadingZero(value));
      }
    },
    [onChange, trackLteWithoutGte, trackChange, answerField, required, values],
  );

  return (
    <QuestionLayoutContainer
      title={title}
      onSubmit={handleSubmit}
      disableSubmit={isSubmitDisabled}
      description={helpText?.text}
    >
      <RangeQuestion
        unit={unit}
        gteValue={pathOr('', ['gte'], values)}
        gteError={errors['gte']}
        gtePlaceholder={'от'}
        lteValue={pathOr('', ['lte'], values)}
        lteError={errors['lte']}
        ltePlaceholder={'до'}
        handleChange={handleChange}
        lteInputName={`${answerField}_lte`}
        gteInputName={`${answerField}_gte`}
      />
    </QuestionLayoutContainer>
  );
};

RangeQuestionContainer.displayName = 'RangeQuestionContainer';
